import { Injectable, OnDestroy, OnInit } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Observable, Subject, throwError, BehaviorSubject } from "rxjs";
import { map, take, takeUntil, tap } from "rxjs/operators";
import { mergeMap, switchMap, catchError } from "rxjs/operators";
import { ConfigurationService } from "./configuration.service";
import { LocalStoreManager } from "./local-store-manager.service";
import { User, LoginResponse, Role, Branch, UserLogin } from "../models";
import { Router, NavigationExtras } from "@angular/router";
import { MvcControllerInfo, MvcAreaInfo } from "../models/mvccontrollerInfo.model";
import { Utilities } from "./utilities";
import { FiscalYear } from "../models/fiscal-year.model";
import { ShowToastrService } from "./show-toastr.service";

import { TokenService } from "./token.service";
import { UserInformation } from "../models/userinfo.model";
import { LicenseInformation } from "../models/license-information.model";
import { DBkeys } from "./db-keys";
import { ClearableCache } from "../models/clearablecache.model";
@Injectable({
  providedIn: "root",
})
export class AuthService implements OnDestroy, OnInit {
  public ngUnsubscribe = new Subject<void>();
  public menuIsRefreshed: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public absRole = new BehaviorSubject<Role[]>([]);
  public isLoadingUserRoles: boolean = false;
  public loginRedirectUrl: string;

  public static loggingUserName: string = "";

  private taskPauser: Subject<any>;
  private _loginStatus = new Subject<boolean>();
  private currentFiscalYear$: BehaviorSubject<FiscalYear> = new BehaviorSubject<FiscalYear>(null);

  private readonly _loginUrl: string = "/api/Authorization/Login";
  private readonly _logoutRedirectUrl: string = "api/Authorization/Logout";
  private fiscalYearLoop = 0;
  private isRefreshingLogin: boolean;
  private isMvcControllerLoading = false;
  private isCurrentFiscalYear = false;
  private isCurrentInformationLoading = false;
  private previousIsLoggedInCheck = false;
  private PayCurrentYearMonthIsRead = false;
  branchIdLoading: boolean;

  get currentBranch(): Branch {
    const branchId = JSON.parse(localStorage.getItem("BranchId"));
    if (!this.localStorage.getDataObject<Branch>(`${DBkeys.CURRENT_BRANCH}_${AuthService.loggingUserName}`)) this.getBranchById(branchId);
    return this.localStorage.getDataObject<Branch>(`${DBkeys.CURRENT_BRANCH}_${AuthService.loggingUserName}`);
  }

  public get currentBranchId(): number {
    const branchId = JSON.parse(localStorage.getItem("BranchId"));
    if (branchId) return branchId;
    return 0;
  }

  public get currentUser(): User {
    let user = this.localStorage.getDataObject<User>(`${DBkeys.CURRENT_USER}_${AuthService.loggingUserName}`);
    this.reevaluateLoginStatus(user);
    return user;
  }

  public get currentUserInfo(): UserLogin {
    if (!this.localStorage.getDataObject<UserLogin>(`${DBkeys.CURRENT_USER_INFO}_${AuthService.loggingUserName}`)) return undefined;
    return this.localStorage.getDataObject<UserLogin>(`${DBkeys.CURRENT_USER_INFO}_${AuthService.loggingUserName}`);
  }

  public get currentFiscalYearobsr(): Observable<FiscalYear> {
    return this.currentFiscalYear$.asObservable();
  }

  get currentFiscalYear() {
    if (this.localStorage.getDataObject<FiscalYear>(`${DBkeys.CURRENT_FISCAL_YEAR}_${AuthService.loggingUserName}`))
      return this.localStorage.getDataObject<FiscalYear>(`${DBkeys.CURRENT_FISCAL_YEAR}_${AuthService.loggingUserName}`);
    else this.saveFiscalYear();
    return this.localStorage.getDataObject<FiscalYear>(`${DBkeys.CURRENT_FISCAL_YEAR}_${AuthService.loggingUserName}`);
  }

  public get rememberMe(): boolean {
    return this.tokenService.rememberMe() === true;
  }

  private get loginUrl(): string {
    return this.configurations.baseUrl + this._loginUrl;
  }

  private get homeUrl(): string {
    return this.configurations.homeUrl;
  }

  private get logoutUrl(): string {
    return this.configurations.baseUrl + this._logoutRedirectUrl;
  }

  private get refreshToken(): string {
    this.reevaluateLoginStatus();
    return this.localStorage.getData(`${DBkeys.REFRESH_TOKEN}_${AuthService.loggingUserName}`);
  }

  constructor(
    protected router: Router,
    protected http: HttpClient,
    protected configurations: ConfigurationService,
    public localStorage: LocalStoreManager,
    private tokenService: TokenService,
    protected sts: ShowToastrService
  ) {
    if (!AuthService.loggingUserName && window.name) AuthService.loggingUserName = window.name;
  }

  public ngOnDestroy(): void {
    if (this.ngUnsubscribe) {
      this.ngUnsubscribe.next();
      this.ngUnsubscribe.complete();
    }
  }

  public ngOnInit(): void {
    this.initializeLoginStatus();
  }

  public getUrls(value: string): string {
    if (this.localStorage.getDataObject<string[]>(DBkeys.ACTIVE_URLS) === null) return undefined;
    var list: string[] = this.localStorage.getDataObject<string[]>(DBkeys.ACTIVE_URLS);
    var list2 = list.filter(
      (x) =>
        x === value && !x.includes("Lookup") && !x.includes("/connect/token") && !x.includes("GetMaxCode") && !x.includes("Role/ByUserName/userName")
    );
    if (list2 && list2.length > 2) return list2[0];
    return undefined;
  }

  public freeUrls(): void {
    this.localStorage.deleteData(DBkeys.ACTIVE_URLS);
  }

  public setUrls(value: string): void {
    var _urls: string[];
    if (this.localStorage.getDataObject<string[]>(DBkeys.ACTIVE_URLS) === null) _urls = [];
    else _urls = this.localStorage.getDataObject<string[]>(DBkeys.ACTIVE_URLS);
    _urls.push(value);
    this.localStorage.saveSessionData(_urls, DBkeys.ACTIVE_URLS);
  }

  public removeurl(value: string): void {
    var _urls: string[];
    if (this.localStorage.getDataObject<string[]>(DBkeys.ACTIVE_URLS) != null) {
      _urls = this.localStorage.getDataObject<string[]>(DBkeys.ACTIVE_URLS);
      _urls = _urls.filter((x) => x != value);
      this.localStorage.saveSessionData(_urls, DBkeys.ACTIVE_URLS);
    }
  }

  public currentInformation(): LicenseInformation {
    if (!this.localStorage.getData(DBkeys.CURRENT_INFORMATION) && !this.isCurrentInformationLoading) this.getCurrentInformation();
    return this.localStorage.getData(DBkeys.CURRENT_INFORMATION);
  }

  public userRoles(userName: string): Role[] {
    if (!userName) {
      if (this.currentUser !== undefined) userName = this.currentUser.UserName;
      else userName = AuthService.loggingUserName;
    }
    var list = this.localStorage.getDataObject<Role[]>(DBkeys.USER_ROLES);
    return list;
  }

  public async getLoginUserRoles(userName: string): Promise<void> {
    if (!userName) userName = this.currentUser.UserName;
    return this.http
      .get(`${this.configurations.baseUrl}/api/Role/ByUserName/${userName}`, this.getRequestHeaders())
      .pipe(
        take(1),
        tap((roles: any) => {
          if (roles && roles.length > 0) this.localStorage.saveSessionData(roles, DBkeys.USER_ROLES);
        }),
        catchError((err) => {
          return throwError(this.handleError(err, null));
        })
      )
      .toPromise();
  }

  public getBranchUsers(): Observable<UserInformation[]> {
    return this.http.get<UserInformation[]>(this.configurations.baseUrl + "/api/Authorization/GetUserList", this.getRequestHeaders()).pipe(
      catchError((error) => {
        this.isLoadingUserRoles = false;
        return this.handleError(error, null);
      })
    );
  }

  public getMvcController(): void {
    this.isMvcControllerLoading = true;
    this.http
      .get<Role>(this.configurations.baseUrl + "/api/Authorization/Controllers", this.getRequestHeaders())
      .pipe(take(1))
      .pipe(
        catchError((error) => {
          this.isMvcControllerLoading = false;
          return this.handleError(error, null);
        })
      )
      .subscribe((controlers) => {
        this.localStorage.saveSessionData(controlers, DBkeys.USER_CONTROLLERS);
        this.isMvcControllerLoading = false;
      });
  }

  public getRequestHeaders(): { headers: HttpHeaders | { [header: string]: string | string[] } } {
    let headers = new HttpHeaders({ "Content-Type": "application/json" });
    return { headers: headers };
  }

  public getIsLoggedIn(): boolean {
    if (window.name && !AuthService.loggingUserName) AuthService.loggingUserName = window.name;
    this.configurations.loggingUserName = AuthService.loggingUserName;
    return this.localStorage.getDataObject<User>(`${DBkeys.CURRENT_USER}_${AuthService.loggingUserName}`) != null;
  }

  public processLoginResponse(response: any, rememberMe: boolean, currentBranchId?: number) {
    this.tokenService.setRememberMe(rememberMe);
    this.tokenService.storeToken(response);
    let refreshToken = this.tokenService.getRawToken() || this.refreshToken;
    let tokenExpiryDate = this.tokenService.getTokenExpirationTime();
    let accessTokenExpiry = tokenExpiryDate;
    let user = <User>{
      Id: this.tokenService.getUserId(),
      UserName: this.tokenService.getName()
        ? this.tokenService.getName()
        : this.currentUser
        ? this.currentUser.UserName
        : AuthService.loggingUserName,
      DisplayName: this.tokenService.getDisplayName(),
      Email: this.tokenService.getName(),
      CurrentBranchId: currentBranchId,
      Roles: Array.isArray(this.tokenService.getRole()) ? this.tokenService.getRole() : [this.tokenService.getRole()],
    };
    this.saveUserDetails(user, refreshToken, null, refreshToken, accessTokenExpiry, rememberMe);
    this.reevaluateLoginStatus(user);
    this.tokenService.storeToken(response);
    this.getHrmPayCurentYearMonth();
    return user;
  }

  public refreshLogin(): Observable<User> {
    if (!AuthService.loggingUserName && this.currentUser) AuthService.loggingUserName = this.currentUser.UserName;
    if (window.name && !AuthService.loggingUserName) AuthService.loggingUserName = window.name;
    else if (AuthService.loggingUserName && !window.name) window.name = AuthService.loggingUserName;
    this.configurations.loggingUserName = AuthService.loggingUserName;
    return this.getRefreshLoginEndpoint<LoginResponse>().pipe(
      map((response) => this.processLoginResponse(response, this.rememberMe, this.currentUser.CurrentBranchId))
    );
  }

  public handleError(error, continuation: () => Observable<any>): Observable<any> {
    if (error.status === 401) {
      if (this.isRefreshingLogin) {
        return this.pauseTask(continuation);
      }
      this.isRefreshingLogin = true;
      return this.refreshLogin().pipe(
        mergeMap((data) => {
          this.isRefreshingLogin = false;
          this.resumeTasks(true);
          return continuation();
        }),
        catchError((refreshLoginError) => {
          this.isRefreshingLogin = false;
          this.resumeTasks(false);
          if (
            refreshLoginError.status === 401 ||
            refreshLoginError.status === 503 ||
            (refreshLoginError.url && refreshLoginError.url.toLowerCase().includes(this.loginUrl.toLowerCase()))
          ) {
            this.reLogin();
            return throwError("session expired");
          } else {
            return throwError(refreshLoginError || "server error");
          }
        })
      );
    } else if (error.status === 400 && !error.url.includes("/connect/token")) {
      this.logout();
      this.redirectLogoutUser();
    } else {
      return throwError(error);
    }
  }

  public redirectLogoutUser(): void {
    let redirect = this.logoutUrl ? this.logoutUrl : this.loginUrl;
    this.router.navigate([redirect]);
  }

  public login(userLogin: UserLogin, token: string): void {
    let objectEnum = Object.keys(ClearableCache);
    objectEnum.forEach((pro) => {
      this.localStorage.deleteData(pro);
    });
    this.localStorage.deleteData(DBkeys.USER_ROLES);
    AuthService.loggingUserName = userLogin.UserName;
    window.name = AuthService.loggingUserName;
    this.configurations.loggingUserName = AuthService.loggingUserName;
    const branchId = JSON.parse(localStorage.getItem("BranchId"));
    this.getBranchById(branchId);
    this.localStorage.savePermanentData(userLogin, `${DBkeys.CURRENT_USER_INFO}_${AuthService.loggingUserName}`);
    this.processLoginResponse(token, userLogin.RememberMe, userLogin.BranchId);
  }

  public logout(): void {
    const current_information = this.localStorage.getData(DBkeys.CURRENT_INFORMATION);
    const computerName = this.localStorage.getData(ClearableCache.ComputerName);
    this.localStorage.clearAllStorage();
    this.localStorage.savePermanentData(current_information, DBkeys.CURRENT_INFORMATION);
    this.localStorage.savePermanentData(computerName, ClearableCache.ComputerName);
    if (this.currentUserInfo) this.localStorage.savePermanentData(this.currentUserInfo, `${DBkeys.CURRENT_USER_INFO}_${AuthService.loggingUserName}`);
    this.configurations.clearLocalChanges();
    this.reevaluateLoginStatus();
  }

  public userControllers(): Observable<MvcAreaInfo[]> {
    if (!this.localStorage.getDataObject<MvcControllerInfo[]>(DBkeys.USER_CONTROLLERS) && !this.isMvcControllerLoading) this.getMvcController();
    let absMvcControllerInfo = new BehaviorSubject<MvcAreaInfo[]>([]);
    if (this.localStorage.getDataObject<MvcControllerInfo[]>(DBkeys.USER_CONTROLLERS))
      absMvcControllerInfo.next(this.localStorage.getDataObject<MvcAreaInfo[]>(DBkeys.USER_CONTROLLERS));
    return absMvcControllerInfo.asObservable();
  }

  public getBranchById(id: number): void {
    if (!this.branchIdLoading) {
      this.branchIdLoading = true;
      this.http
        .get<Role>(this.configurations.baseUrl + "/api/Branch/GetById/" + id, this.getRequestHeaders())
        .pipe(takeUntil(this.ngUnsubscribe))
        .pipe(
          catchError((error) => {
            this.branchIdLoading = false;
            return this.handleError(error, null);
          })
        )
        .subscribe((branch) => {
          this.localStorage.saveSessionData(branch, `${DBkeys.CURRENT_BRANCH}_${AuthService.loggingUserName}`);
          this.branchIdLoading = false;
        });
    }
  }

  public saveFiscalYear(currentYear: FiscalYear = undefined): void {
    if (currentYear !== undefined) {
      this.currentFiscalYear$.next(currentYear);
      this.localStorage.savePermanentData(currentYear, `${DBkeys.CURRENT_FISCAL_YEAR}_${AuthService.loggingUserName}`);
    } else if (!this.isCurrentFiscalYear && this.fiscalYearLoop < 1) {
      this.isCurrentFiscalYear = true;
      this.http
        .get<Role>(this.configurations.baseUrl + "/api/FiscalYear/FiscalYear/", this.getRequestHeaders())
        .pipe(
          take(1),
          catchError((error) => {
            this.isCurrentFiscalYear = false;
            return this.handleError(error, null);
          })
        )
        .subscribe(
          (fiscalYear) => {
            this.isCurrentFiscalYear = false;
            if (fiscalYear) {
              const now = new Date();
              const currentFiscalYear = fiscalYear.find(
                (x) =>
                  new Date(x.StartDate).setHours(0, 0, 0, 0) <= now.setHours(0, 0, 0, 0) &&
                  new Date(x.EndDate).setHours(0, 0, 0, 0) >= now.setHours(0, 0, 0, 0)
              );
              if (!currentFiscalYear) {
                this.sts.showDialog("سال مالی تعریف نشده است.", "خطای سال مالی");
                this.fiscalYearLoop += 1;
              }
              this.currentFiscalYear$.next(currentFiscalYear);
              this.localStorage.savePermanentData(currentFiscalYear as FiscalYear, `${DBkeys.CURRENT_FISCAL_YEAR}_${AuthService.loggingUserName}`);
            }
          },
          (err) => (this.isCurrentFiscalYear = false)
        );
    }
  }

  public hasLocalSetting(): Observable<boolean> {
    let absRole = new BehaviorSubject<boolean>(undefined);
    if (this.localStorage.getData(DBkeys.HAS_LOCAL_SETTING) !== undefined && this.localStorage.getData(DBkeys.HAS_LOCAL_SETTING) !== null) {
      absRole.next(this.localStorage.getDataObject<boolean>(DBkeys.HAS_LOCAL_SETTING));
    } else {
      const computerName: string = this.localStorage.getData(ClearableCache.ComputerName);
      this.http
        .get<any>(
          this.configurations.baseUrl + "/api/LocalSetting/HasLocalSetting" + (computerName ? "?ComputerName=" + computerName : ""),
          this.getRequestHeaders()
        )
        .pipe(
          take(1),
          catchError((error) => {
            return this.handleError(error, null);
          })
        )
        .subscribe((result) => {
          absRole.next(result);
          this.localStorage.saveSessionData(result as boolean, DBkeys.HAS_LOCAL_SETTING);
        });
    }
    return absRole;
  }

  public getHrmPayCurentYearMonth(): any {
    if (!this.localStorage.getData(DBkeys.PAY_CURENTYEAR_MONTH)) this.saveHrmPayCurentYearMonth();
    return this.localStorage.getData(DBkeys.PAY_CURENTYEAR_MONTH);
  }

  public saveHrmPayCurentYearMonth(): void {
    if (this.PayCurrentYearMonthIsRead) return;
    this.PayCurrentYearMonthIsRead = true;
    this.http
      .get<any>(this.configurations.baseUrl + "/api/MonthlySalaryCalculationInformation/GetHrmPayCurentYearMonth", this.getRequestHeaders())
      .pipe(
        take(1),
        catchError((error) => {
          this.PayCurrentYearMonthIsRead = false;
          return this.handleError(error, null);
        })
      )
      .subscribe((result) => {
        this.PayCurrentYearMonthIsRead = false;
        this.localStorage.saveSessionData(result, DBkeys.PAY_CURENTYEAR_MONTH);
      });
  }

  private reevaluateLoginStatus(currentUser?: User): void {
    let user = currentUser || this.localStorage.getDataObject<User>(`${DBkeys.CURRENT_USER}_${AuthService.loggingUserName}`);
    this.configurations.clearLocalChanges();
    let isLoggedIn = user != null;
    if (this.previousIsLoggedInCheck != isLoggedIn) {
      this._loginStatus.next(isLoggedIn);
    }
    this.previousIsLoggedInCheck = isLoggedIn;
  }

  private getCurrentInformation(): void {
    this.isCurrentInformationLoading = true;
    this.http
      .get<Role>(this.configurations.baseUrl + "/api/Home/CurrentInformation", this.getRequestHeaders())
      .pipe(
        take(1),
        catchError((error) => {
          this.isCurrentInformationLoading = false;
          return this.handleError(error, null);
        })
      )
      .subscribe((controlers) => {
        this.localStorage.savePermanentData(controlers, DBkeys.CURRENT_INFORMATION);
        this.isCurrentInformationLoading = false;
      });
  }

  private saveUserDetails(user: User, accessToken: string, idToken: string, refreshToken: string, expiresIn: Date, rememberMe: boolean): void {
    if (rememberMe) {
      this.localStorage.savePermanentData(accessToken, `${DBkeys.ACCESS_TOKEN}_${AuthService.loggingUserName}`);
      this.localStorage.savePermanentData(idToken, `${DBkeys.ID_TOKEN}_${AuthService.loggingUserName}`);
      this.localStorage.savePermanentData(refreshToken, `${DBkeys.REFRESH_TOKEN}_${AuthService.loggingUserName}`);
      this.localStorage.savePermanentData(expiresIn, `${DBkeys.TOKEN_EXPIRES_IN}_${AuthService.loggingUserName}`);
      this.localStorage.savePermanentData(user, `${DBkeys.CURRENT_USER}_${AuthService.loggingUserName}`);
    } else {
      this.localStorage.saveSyncedSessionData(accessToken, `${DBkeys.ACCESS_TOKEN}_${AuthService.loggingUserName}`);
      this.localStorage.saveSyncedSessionData(idToken, `${DBkeys.ID_TOKEN}_${AuthService.loggingUserName}`);
      this.localStorage.saveSyncedSessionData(refreshToken, `${DBkeys.REFRESH_TOKEN}_${AuthService.loggingUserName}`);
      this.localStorage.saveSyncedSessionData(expiresIn, `${DBkeys.TOKEN_EXPIRES_IN}_${AuthService.loggingUserName}`);
      this.localStorage.saveSyncedSessionData(user, `${DBkeys.CURRENT_USER}_${AuthService.loggingUserName}`);
    }
  }

  private reLogin(): void {
    this.localStorage.deleteData(`${DBkeys.TOKEN_EXPIRES_IN}_${AuthService.loggingUserName}`);
    this.redirectForLogin();
  }

  private redirectForLogin(): void {
    if (this.localStorage.getDataObject<UserLogin>(`${DBkeys.CURRENT_USER_INFO}_${AuthService.loggingUserName}`))
      this.login(this.localStorage.getDataObject<UserLogin>(`${DBkeys.CURRENT_USER_INFO}_${AuthService.loggingUserName}`), null);
    else this.redirectLoginUser();
  }

  private initializeLoginStatus(): void {
    this.localStorage
      .getInitEvent()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.reevaluateLoginStatus();
      });
  }

  private redirectLoginUser(): void {
    let redirect =
      this.loginRedirectUrl && this.loginRedirectUrl != "/" && this.loginRedirectUrl != ConfigurationService.defaultHomeUrl
        ? this.loginRedirectUrl
        : this.homeUrl;
    this.loginRedirectUrl = null;
    let urlParamsAndFragment = Utilities.splitInTwo(redirect, "#");
    let urlAndParams = Utilities.splitInTwo(urlParamsAndFragment.firstPart, "?");
    let navigationExtras: NavigationExtras = {
      fragment: urlParamsAndFragment.secondPart,
      queryParams: Utilities.getQueryParamsFromString(urlAndParams.secondPart),
      queryParamsHandling: "merge",
    };
    this.router.navigate([this._loginUrl]);
  }

  private getRefreshLoginEndpoint<T>(): Observable<T> {
    let header = new HttpHeaders({ "Content-Type": "application/x-www-form-urlencoded" });
    let params = new HttpParams()
      .append("refresh_token", this.refreshToken)
      .append("grant_type", "refresh_token")
      .append("scope", "openid email phone profile offline_access roles");
    let requestBody = params.toString();
    return this.http
      .post<T>(this.loginUrl, requestBody, { headers: header })
      .pipe(take(1))
      .pipe<T>(
        catchError((error) => {
          return this.handleError(error, () => this.getRefreshLoginEndpoint());
        })
      );
  }

  private pauseTask(continuation: () => Observable<any>): Observable<any> {
    if (!this.taskPauser) this.taskPauser = new Subject();
    return this.taskPauser.pipe(
      switchMap((continueOp) => {
        return continueOp ? continuation() : throwError("session expired");
      })
    );
  }

  private resumeTasks(continueOp: boolean): void {
    setTimeout(() => {
      if (this.taskPauser) {
        this.taskPauser.next(continueOp);
        this.taskPauser.complete();
        this.taskPauser = null;
      }
    });
  }
}
